import { subject } from "@casl/ability";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ActionSend from "assets/img/svg/actionSend";
import { DownloadOutline } from "assets/img/svg/downloadOutline";
import AgreementModal from "components/shared/AgreementModal";
import BlockCards from "components/shared/BlockCards";
import ChipsAutocomplete from "components/shared/ChipsAutocomplete";
import CompletionDateModal from "components/shared/CompletionDateModal";
import ControlFilter from "components/shared/ControlFilter";
import type { FilterOrder } from "components/shared/ElementList/sortStorage";
import BulkActionsMenu from "components/shared/LastElementTable/BulkActionsMenu";
import DownloadFilesModal from "components/shared/LastElementTable/BulkActionsMenu/DownloadFilesModal";
import type { LastElementTableAPIData } from "components/shared/LastElementTable/type";
import ViewColumnOptions from "components/shared/LastElementTable/ViewColumnOptions";
import OrderFilter from "components/shared/ListFilters/OrderFilter";
import ModalBase from "components/shared/ModalBase";
import MultiDataFilters from "components/shared/MultiDataFilters";
import SendEmail from "components/shared/SendEmail";
import { useSelectedArticlesDataTransform } from "components/shared/SendEmail/helpers";
import SmallSearchBar from "components/shared/SmallSearchBar";
import StatusSelect from "components/shared/StatusSelect";
import type { PaymentOption } from "components/shared/StatusSelect/utils";
import { paymentColorRules, paymentOptions } from "components/shared/StatusSelect/utils";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { completionStatusFilterOptions } from "containers/ArticleFormContainer/utils";
import type { NewAgreementLocationState } from "containers/ContractValidationNew";
import { PortalType } from "contexts/portal.context";
import { PanelTitle } from "helpers/TypographyComponents";
import { useAbility } from "hooks/useAbility";
import type { TPaidStatus } from "hooks/useAPIPagination";
import useAPIPagination from "hooks/useAPIPagination";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useCompanySettings } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import { useSignatureProvider } from "hooks/useSignatureProvider";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchAgreements } from "store/agreementsStore";
import type { AgreementStatus } from "store/agreementsStore";
import type { ArticlePaidPayload, ManagementArticle } from "store/articleStore";
import {
    ArticleCompletionStatus,
    putArticleCompletionStatus,
    putArticlePaid,
    putArticlePaidBulk,
    setLastSentAtTerms,
} from "store/articleStore";
import { getBeneficiaryTags } from "store/beneficiaryStore";
import { fetchPressStats, fetchSendStatement } from "store/managementStore";
import { getServices } from "store/servicesStore";
import { fetchCompanyInfo } from "store/settingsStore";
import { sendBulkMailWithStatement } from "store/statementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTags } from "store/titleStore";
import type { AgreementSignedStatus } from "store/types";
import { PaymentStatus } from "store/types";
import type { TSortOrder } from "types";
import { EStatementType } from "types";

import ArticleDrawer from "./ArticleDrawer";
import PressManagementTable from "./PressManagementTable";
import styles from "./style.module.scss";
import { usePressManagementUtilsData } from "./utils";
/**
 * Press management page component
 */
const PressManagement = () => {
    const { allArticles } = useAppSelector((state) => state.article);
    const { getCompanySettings } = useCompanySettings();
    const { companySettings } = useAppSelector((state) => state.settings);
    const { servicesStatus } = useAppSelector((state) => state.services);

    const { hasPermissions } = usePermissions();
    const ability = useAbility();

    const usesIRCEC = getCompanySettings("uses_ircec");
    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");
    const usesAgreements = !!getCompanySettings("uses_agreements");

    const activeSignProvider = useActiveSignProvider();
    const { isDocoon } = useSignatureProvider(activeSignProvider.key);

    const { stats } = useAppSelector((state) => state.management);
    const tagTitleList = useAppSelector((state) => state.titles.tags);
    const tagBeneficiaryList = useAppSelector((state) => state.beneficiary.tags);

    const dispatch = useAppDispatch();
    const [t] = useTranslation();
    const portalType = useContext(PortalType) ?? "press";
    const navigate = useNavigate();

    const [currentArticle, setCurrentArticle] = useState<ManagementArticle | null>();
    const [selectedArticles, setSelectedArticles] = useState<number[]>([]);

    const [showArticleDrawer, setShowArticleDrawer] = useState(false);

    const [pendingPaymentArticle, setPendingPaymentArticle] = useState<{
        titleId: number;
        articleId: number;
        paidStatus: 2;
    } | null>(null);

    type Modals = "send_email" | "download" | "agreement" | "completion_date" | "payment_date";
    const [modalType, setModalType] = useState<Modals | "">("");
    const [currentArticleForCompletion, setCurrentArticleForCompletion] = useState<{
        completionStatus: ArticleCompletionStatus;
        titleId: number;
        articleId: number;
    } | null>(null);

    const selectedArticlesCount = selectedArticles.length;

    useEffect(() => {
        dispatch(fetchPressStats());
        dispatch(fetchAgreements());
        !companySettings && dispatch(fetchCompanyInfo());
        if (usesIRCEC && servicesStatus === "idle") {
            // Fetch IRCEC services data for Article form
            dispatch(getServices());
        }
        if (tagTitleList === null) {
            dispatch(fetchTags());
        }
        if (tagBeneficiaryList === null) {
            dispatch(getBeneficiaryTags());
        }
    }, []);

    const {
        query,
        limitPagination,
        page,
        count,
        sortBy,
        sortOrder,
        handleQuery,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        setSortBy,
        setSortOrder,
        setPaidStatus,
        requestStatus,
        setRequestStatus,
        setSignedStatus,
        chosenTitleTags,
        setChosenTitleTags,
        chosenBeneficiaryTags,
        setChosenBeneficiaryTags,
        completionStatus,
        setCompletionStatus,
        setAgreementStatus,
        handleRequest,
    } = useAPIPagination({ type: "articles", sortBy: "createdAt", sortOrder: "desc" });

    const APIData: LastElementTableAPIData = {
        count,
        page,
        limitPagination,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        setRequestStatus,
    };

    const managementOptions: ThreeDotMenuOptions[] = [
        {
            id: "export-dap",
            icon: <DownloadOutline width="16" height="16" />,
            name: t("Management.DAPExport"),
            action: () => {
                handleRequest({ defaultType: "articles-download-dap" });
            },
        },
    ];

    const { blockCardsData, orderOptions, filtersGroups } = usePressManagementUtilsData(
        stats,
        portalType,
        usesAgreementValidation,
    );
    const { sendEmailTermData, articlesForAgreementsData } =
        useSelectedArticlesDataTransform(allArticles?.entries, selectedArticles) ?? {};

    /**
     * Chose filter option
     * @param value chosen option value
     * @param groupLabel chosen option group label
     */
    const handleChoseFilter = (value: string, groupLabel: string) => {
        switch (groupLabel) {
            case t("PressAudio.Management.PaymentStatus"):
                setPaidStatus(value as TPaidStatus);
                break;

            case t("PressAudio.Management.ContractStatus"): {
                const isSignedStatus = ["tosend", "signed"].includes(value);

                if (usesAgreementValidation && !isSignedStatus) {
                    setAgreementStatus(value as AgreementStatus);
                    // reset the other cond. state avoid wrong filter query because it's only one accepted filter (it can't be both)
                    setSignedStatus("all");
                } else {
                    setSignedStatus(value as AgreementSignedStatus);
                    setAgreementStatus("all");
                }
            }
        }
    };

    const handleChoseOrder = (orderOptions: FilterOrder) => {
        const { sortBy, sortOrder } = orderOptions;
        setSortBy(sortBy);
        setSortOrder(sortOrder as TSortOrder);
    };

    const handleSelectRows = (articleIds: number[]) => {
        setSelectedArticles(articleIds);
    };

    /**
     * Handle download statements -> send downloaded statements via email
     */
    const handleSendStatements = () => {
        dispatch(
            fetchSendStatement({
                articles: selectedArticles,
                host: window.location.origin,
            }),
        );
        setModalType("");
    };

    /**
     * Handle send statements by email
     */
    const handleSendEmail = (
        emails: string[],
        subject: string,
        body: string,
        _ignore?: EStatementType[],
        subjectByLang?: Record<string, string>,
        bodyByLang?: Record<string, string>,
    ) => {
        dispatch(
            sendBulkMailWithStatement({
                articleId: selectedArticles,
                mailCc: emails,
                subject,
                body,
                subjectByLang,
                bodyByLang,
            }),
        );
        dispatch(setLastSentAtTerms(selectedArticles));
        setModalType("");
    };

    /**
     * Change payment status for selected article (bulk) or for a single payment
     * @param type "single" or "bulk"
     * @param paidStatus selected paidStatus value
     * @param articleIds selected articles Ids (bulk)
     * @param titleId selected article, parent title id
     * @param articleId selected article id (single)
     */
    const handleChangePaymentStatus = (
        type: "single" | "bulk",
        paidStatus: PaymentStatus,
        articleIds?: number[],
        titleId?: number,
        articleId?: number,
    ) => {
        switch (type) {
            case "single":
                if (paidStatus === PaymentStatus.PAID) {
                    setPendingPaymentArticle({ titleId: titleId!, articleId: articleId!, paidStatus });
                    setModalType("payment_date");
                } else {
                    dispatch(
                        putArticlePaid({
                            titleId,
                            articleId,
                            paymentData: { paidStatus, paymentDate: null, from: "management" },
                        } as ArticlePaidPayload),
                    );
                }
                break;
            case "bulk": {
                let articlesIdsToUpdate = articleIds;
                // If we want to pay articles, don't change paid articles (or it will change the payment date)
                if (paidStatus === PaymentStatus.PAID) {
                    if (!allArticles?.entries.length) return;
                    const paidArticlesSet = new Set(
                        allArticles.entries
                            .filter((article) => article.paidStatus === PaymentStatus.PAID)
                            .map((article) => article.id),
                    );
                    articlesIdsToUpdate = articleIds?.filter((id) => !paidArticlesSet.has(id));
                }
                if (!articlesIdsToUpdate?.length) return;
                dispatch(putArticlePaidBulk({ id: articlesIdsToUpdate, status: paidStatus }));
            }
        }
    };

    const handleShowArticle = (_articleId: number, _id2: any, _type: any, article: ManagementArticle) => {
        setCurrentArticle(article);
        setShowArticleDrawer(true);
    };

    const handleShowAgreementModal = () => {
        if (!currentArticle) return;
        if (usesAgreementValidation || isDocoon) {
            const { id, beneficiaryId, name: title, beneficiary, titleId, email } = currentArticle;
            const state = {
                relatedResourceData: {
                    relatedResource: "article",
                    relatedResourceId: id,
                    beneficiaryId,
                    title,
                    titleId,
                    beneficiary,
                    email,
                },
                signatureProvider: activeSignProvider.key,
            } as NewAgreementLocationState;
            navigate("/agreements/new", { state });
            return;
        }
        setModalType("agreement");
    };

    // Filter table by completion status
    const handleCompletionStatusChange = (value: ArticleCompletionStatus | "all") => {
        setCompletionStatus(value);
    };

    // Update article completion status
    const updateArticleCompletionStatus = (
        completionStatus: ArticleCompletionStatus,
        titleId: number,
        articleId: number,
    ) => {
        // If changing to "completed", show modal to ask for completion date
        if (completionStatus === ArticleCompletionStatus.COMPLETED) {
            setCurrentArticleForCompletion({ completionStatus, titleId, articleId });
            setModalType("completion_date");
        } else {
            // If changing to "pending", update without date
            dispatch(
                putArticleCompletionStatus({
                    completionStatus,
                    completionDate: null,
                    titleId,
                    articleId,
                    from: "management",
                }),
            );
        }
    };

    const handleCompletionDateSubmit = (completionDate: Date) => {
        if (!currentArticleForCompletion) return;
        dispatch(
            putArticleCompletionStatus({
                ...currentArticleForCompletion,
                completionDate,
                from: "management",
            }),
        );
        setModalType("");
        setCurrentArticleForCompletion(null);
    };

    const handlePaymentDateSubmit = (date: Date) => {
        if (!pendingPaymentArticle) return;
        const { titleId, articleId, paidStatus } = pendingPaymentArticle;
        dispatch(
            putArticlePaid({
                titleId,
                articleId,
                paymentData: { paidStatus, paymentDate: date, from: "management" },
            } as ArticlePaidPayload),
        );
        setModalType("");
        setPendingPaymentArticle(null);
    };

    const handlePaymentDateClose = () => {
        setModalType("");
        setPendingPaymentArticle(null);
    };

    const handleCompletionDateClose = () => {
        setModalType("");
        setCurrentArticleForCompletion(null);
    };

    const showBulkPaymentStatusSelect = () => {
        return ability.can("update", "ArticlePayment");
    };

    const dropdownPaymentOptions = (): PaymentOption[] => {
        // High level access: can set all payment options
        if (ability.can("access", subject("ArticlePaymentOptions", { level: 2 }))) {
            return paymentOptions();
        } else if (ability.can("access", subject("ArticlePaymentOptions", { level: 1 }))) {
            return [
                { label: t("Articles.NotPaid"), value: PaymentStatus.NOT_PAID },
                { label: t("Articles.ToPay"), value: PaymentStatus.TO_PAY },
            ];
        }

        return [];
    };

    return (
        <div className={styles["management-press"]}>
            {/** HEADER */}
            <div className={styles["header"]}>
                <div className={styles["header-left"]}>
                    <PanelTitle>{t(`PressAudio.Management.PageTitle_${portalType}`)}</PanelTitle>
                </div>
                <div className={styles["header-right"]}>
                    <div className={styles["search"]}>
                        <SmallSearchBar filter={query} onSearch={handleQuery} />
                        <OrderFilter
                            orderOptions={orderOptions}
                            onSelection={handleChoseOrder}
                            chosenOrder={{ sortBy: sortBy || "createdAt", sortOrder }}
                        />
                        <MultiDataFilters filterGroups={filtersGroups} handleChoseFilters={handleChoseFilter} />
                    </div>
                    {/** New Publication Btn */}
                    <StyledButton
                        buttonType="primary"
                        className={styles["new-article-btn"]}
                        disabled={!hasPermissions(["article:new"])}
                        tooltipText={!hasPermissions(["article:new"]) && t("LogIn.NotAuthorizedException")}
                        onClick={() => navigate("/articles/new", { state: { from: "management" } })}
                    >
                        <AddCircleOutlineOutlinedIcon />
                        {t(`PressAudio.Management.NewArticle_${portalType}`)}
                    </StyledButton>
                    <ThreeDotMenu options={managementOptions} />
                </div>
            </div>

            {/** STATS */}
            <BlockCards data={blockCardsData} className={styles["management-press-block-cards"]} />

            {/** FILTERS */}
            <div className={styles["management-press-filters"]}>
                <div className={styles["filters-left"]}>
                    <ChipsAutocomplete
                        label={t(`Filters.TitlesLabel_${portalType}`)}
                        filter={query}
                        options={tagTitleList ?? []}
                        noOptionsText={t(`Filters.EmptyStateTitleTags_${portalType}`)}
                        chosenTags={chosenTitleTags}
                        setChosenTags={setChosenTitleTags}
                    />
                    <ChipsAutocomplete
                        label={t("Filters.Beneficiaries")}
                        filter={query}
                        options={tagBeneficiaryList ?? []}
                        noOptionsText={t("Filters.EmptyStateBeneficiaryTags")}
                        chosenTags={chosenBeneficiaryTags}
                        setChosenTags={setChosenBeneficiaryTags}
                    />
                </div>
                <div className={styles["filters-right"]}>
                    <ControlFilter
                        filterLabel={t("Filters.CompletionStatusLabel")}
                        controlValue={completionStatus}
                        onValueChange={handleCompletionStatusChange}
                        filterOptions={completionStatusFilterOptions()}
                    />
                    <ViewColumnOptions tableType="press-management-table" />
                </div>
            </div>

            {/** BULK ACTIONS MENU */}
            <BulkActionsMenu
                show={selectedArticlesCount > 0}
                selectedRowsCount={selectedArticlesCount}
                style={{ marginTop: "1rem" }}
            >
                <div className={styles["management-press-bulk-actions"]}>
                    <div className={styles["actions-left"]}>
                        <StyledButton size="small" buttonType="secondary" onClick={() => setModalType("download")}>
                            <FileDownloadOutlinedIcon style={{ height: "1rem", width: "1rem" }} />
                            {t("ShowStatements.Download")}
                        </StyledButton>
                        <StyledButton size="small" buttonType="secondary" onClick={() => setModalType("send_email")}>
                            <ActionSend style={{ height: "0.8rem", width: "0.8rem" }} />
                            {t("Articles.SendStatement", { count: selectedArticlesCount })}
                        </StyledButton>
                    </div>
                    <div className={styles["actions-right"]}>
                        {usesAgreements && (
                            <StyledButton size="small" buttonType="secondary" onClick={handleShowAgreementModal}>
                                <ActionSend style={{ height: "0.8rem", width: "0.8rem" }} />
                                {t("Agreements.BtnSendContratsToSign")}
                            </StyledButton>
                        )}
                        {showBulkPaymentStatusSelect() && (
                            <StatusSelect
                                options={paymentOptions()}
                                fullWidth
                                onChange={(paidStatus: PaymentStatus) =>
                                    handleChangePaymentStatus("bulk", paidStatus, selectedArticles)
                                }
                                colorRules={paymentColorRules}
                                dropdownOptions={dropdownPaymentOptions()}
                            />
                        )}
                    </div>
                </div>
            </BulkActionsMenu>

            {/** MANAGEMENT TABLE */}
            <div style={{ marginTop: selectedArticlesCount ? "0.5rem" : "1rem" }}>
                <PressManagementTable
                    APIData={APIData}
                    articlesData={allArticles?.entries}
                    isLoading={requestStatus !== "succeeded"}
                    dataCount={allArticles?.count}
                    handleSelectRows={handleSelectRows}
                    handleChangePaymentStatus={handleChangePaymentStatus}
                    handleQuery={handleQuery}
                    handleShowArticle={handleShowArticle}
                    usesAgreementValidation={usesAgreementValidation}
                    updateArticleCompletionStatus={updateArticleCompletionStatus}
                    dropdownPaymentOptions={dropdownPaymentOptions()}
                />
            </div>

            {/** Open Articles details drawer */}
            {allArticles?.entries && (
                <ArticleDrawer
                    open={showArticleDrawer}
                    onClose={() => setShowArticleDrawer(false)}
                    articlesFromTable={allArticles?.entries}
                    currentArticle={currentArticle}
                    setCurrentArticle={setCurrentArticle}
                    from="management"
                    handleShowAgreementModal={handleShowAgreementModal}
                />
            )}
            {/** Bulk Actions Modals */}
            <DownloadFilesModal
                open={modalType === "download"}
                onClose={() => setModalType("")}
                selectedItemsCount={selectedArticlesCount}
                modalTitle={t("Management.DownloadStatement")}
                handleDownload={handleSendStatements}
            />
            {/** Completion Date Modal */}
            <ModalBase
                modalTitle={t("Articles.CompletionDateModalTitle")}
                modalSubtitle={t("Articles.CompletionDateModalSubtitle")}
                open={modalType === "completion_date"}
                onClose={handleCompletionDateClose}
            >
                <CompletionDateModal
                    onSubmit={handleCompletionDateSubmit}
                    onClose={handleCompletionDateClose}
                    defaultDate={new Date()}
                />
            </ModalBase>
            {/** Payment Date Modal */}
            <ModalBase
                modalTitle={t("Articles.PaymentDateModalTitle")}
                modalSubtitle={t("Articles.PaymentDateModalSubtitle")}
                open={modalType === "payment_date"}
                onClose={handlePaymentDateClose}
            >
                <CompletionDateModal
                    onSubmit={handlePaymentDateSubmit}
                    onClose={handlePaymentDateClose}
                    defaultDate={new Date()}
                    label={t("Articles.PaymentDate")}
                />
            </ModalBase>
            {modalType === "send_email" && (
                <SendEmail
                    isModalOpen={modalType === "send_email"}
                    onClose={() => setModalType("")}
                    sendEmail={handleSendEmail}
                    isPress
                    isBulk={selectedArticles.length > 1}
                    showGross={false}
                    termData={sendEmailTermData}
                />
            )}
            {modalType === "agreement" && (
                <AgreementModal // Whithout agreement validation
                    open={modalType === "agreement"}
                    articles={articlesForAgreementsData}
                    article={currentArticle ?? articlesForAgreementsData?.[0]}
                    inBulk={selectedArticlesCount > 1}
                    onClose={() => setModalType("")}
                    from={portalType as "press" | "audiovisual"}
                    pageType="management"
                    relatedResource="article"
                />
            )}
        </div>
    );
};

export default PressManagement;
